import { Injectable } from '@angular/core';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { Observable, combineLatest } from 'rxjs';
import { BcRestApi } from './rest-api';
import { shareReplay } from 'rxjs/operators';
import { BcSettingsService } from 'common/settings/settings.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { AggregationData, CustomFields, SubscriptionData, Visit, Visitor, ExperimentData } from 'shared/components/detailed-data/visit.interface';
import { LoginSubscriber } from 'shared/services/subscribers/login-subscriber.service';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { BcFeatures } from 'common/common/constants/feature-enums';

@Injectable({ providedIn: 'root' })
export class CustomerInformationHubService {
  eventTypes = {
    VISITOR: 'visitor-update',
    VISIT: 'visit-update',
    VISIT_CUSTOM_FIELD: 'visit-custom-field-update',
    VISIT_AGGREGATION_DATA: 'visit-aggregation-data-update',
    EXPERIMENT: 'experiment-update',
    SUBSCRIBE: 'subscribe',
    UNSUBSCRIBE: 'unsubscribe',
    RECONNECT: 'reconnect'
  };

  socket: Socket;
  realTimeServiceUrl: URL;
  activeSubscriptions: SubscriptionData[] = [];

  constructor(private bcRestApi: BcRestApi,
              private bcSettingsService: BcSettingsService,
              private loginSubscriber: LoginSubscriber,
              private logoutSubscriber: LogoutSubscriber
  ) {
    this.loginSubscriber.subscribe(() => {
      this.initialize();
    });
    this.logoutSubscriber.subscribe(() => {
      if (this.socket) {
        this.socket.disconnect();
      }
    });
  }

  async initialize(): Promise<void> {
    const settings = await this.bcSettingsService.getSettings();
    if (this.bcSettingsService.VisitFeatures.isEnabled(settings, BcFeatures.VM_CIH_REAL_TIME_DATA_SYNC) && this.bcSettingsService.VisitFeatures.isEnabled(settings, BcFeatures.VM_CUSTOMER_INFORMATION_HUB)) {
      this.realTimeServiceUrl = new URL(settings.visit.visitorRealTimeServiceUrl);

      const socketIoConfig: SocketIoConfig = {
        url: this.realTimeServiceUrl.origin,
        options: {
          path: this.realTimeServiceUrl.pathname,
          query: { path: `accountIdType=boldAccountId&boldAccountId=${this.bcRestApi.getAccountId()}` },
          transports: ['websocket']
        }
      };

      this.socket = this.createSocket(socketIoConfig);
      this.socket.connect();
      this.socket.fromEvent<any>(this.eventTypes.RECONNECT).subscribe(() => {
        this.activeSubscriptions.forEach(sub => {
          this.setRealTimeDataParams(sub.visitorId, sub.visitId, sub.workItemId, sub.workItemType);
        });
      });
    }
  }

  subscribeRealTimeData(visitorId: string, visitId: string, workItemId: string, workItemType: WorkItemTypes): Observable<[Visitor, Visit, CustomFields, AggregationData, ExperimentData]> {
    if (!this.activeSubscriptions.find(subscription => subscription.visitId === visitId)) {
      this.activeSubscriptions.push({ visitorId, visitId, workItemId, workItemType });
    }
    return combineLatest([
      this.socket.fromEvent<Visitor>(`${this.eventTypes.VISITOR}-${visitorId}-${visitId}`),
      this.socket.fromEvent<Visit>(`${this.eventTypes.VISIT}-${visitorId}-${visitId}`),
      this.socket.fromEvent<CustomFields>(`${this.eventTypes.VISIT_CUSTOM_FIELD}-${visitorId}-${visitId}`),
      this.socket.fromEvent<AggregationData>(`${this.eventTypes.VISIT_AGGREGATION_DATA}-${visitorId}-${visitId}`),
      this.socket.fromEvent<ExperimentData>(`${this.eventTypes.EXPERIMENT}-${visitorId}-${visitId}`)
    ]).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  setRealTimeDataParams(visitorId: string, visitId: string, workItemId: string, workItemType: WorkItemTypes): void {
    if (!visitorId || !visitId) {
      throw new Error('Missing visitorId or visitId');
    }

    this.socket.emit(this.eventTypes.SUBSCRIBE, {
      boldVisitorId: visitorId,
      boldVisitId: visitId,
      workItemId: workItemId,
      workItemType: workItemType
    });
  }

  unsubscribeRealTimeData(visitorId, visitId): void {
    this.activeSubscriptions = this.activeSubscriptions.filter(subscription => subscription.visitId !== visitId);
    this.socket.emit(this.eventTypes.UNSUBSCRIBE, { boldVisitorId: visitorId, boldVisitId: visitId });
  }

  createSocket(config: SocketIoConfig): Socket {
    return new Socket(config);
  }
}
